import { useMutation, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import CryptoJS from 'crypto-js';
import { authApi, employeeSignupApi, queryClient } from '~/apis';
import {
  AuthProtocolsCreateClientViewAuthorizationRequest,
  AuthProtocolsGetClientViewClientsResponse,
  AuthProtocolsGetUserInfoResponse,
  AuthProtocolsLoginResponse,
  AuthProtocolsOktaLoginRequest,
  AuthProtocolsOktaRiotLoginRequest,
  AuthProtocolsOktaSamlLoginRequest,
  AuthProtocolsVerifyEmailRequest,
  AuthProtocolsVerifyEmailResponse,
  EmployeeSignupProtocolsGetEmployeeDataWithTokenRequest,
  MarqCommonEnumsClientClientRoleEnum,
} from '~/swagger/data-contracts';
import { ChannelDisplayTypeEnum, clearStorageBrandId, parseChannelDisplayTypeEnum } from '~/utils';

const STORE_SECRET = import.meta.env.REACT_APP_STORE_SECRET || '';
const BROWSER_TOKEN_STORAGE_KEY = '_marq_cc_ui'; // admin-client-view: sessionStorage, pure-client: localStorage

// #region Token 기반 환경 관련 로직
export const checkIsClientView = () => sessionStorage.getItem(BROWSER_TOKEN_STORAGE_KEY) !== null;
// #endregion

// #region Token CRUD 관련 로직
export const QueryKeyAuth = 'auth';

const _parseAuthData = (data: string | null): AuthProtocolsLoginResponse | null => {
  if (data) {
    return JSON.parse(CryptoJS.AES.decrypt(data, STORE_SECRET).toString(CryptoJS.enc.Utf8));
  }

  return null;
};
const _getAuth = (): AuthProtocolsLoginResponse | null => {
  try {
    const data = checkIsClientView()
      ? sessionStorage.getItem(BROWSER_TOKEN_STORAGE_KEY)
      : localStorage.getItem(BROWSER_TOKEN_STORAGE_KEY);
    return _parseAuthData(data);
  } catch (e) {
    clearAuth(true);
    throw new Error('Failed to load user info');
  }
};
const _setAuth = (data: Omit<AuthProtocolsLoginResponse, 'mfa_status'>, refetch?: boolean, isFromAdmin?: boolean) => {
  if (refetch) {
    const queryCache = queryClient.getQueryCache();

    const liveQueriesOnScreen = queryCache.findAll();
    for (let v = 0; v < liveQueriesOnScreen.length; v += 1) {
      const queryKey = liveQueriesOnScreen[v].queryKey;
      if (!(queryKey && queryKey.length === 1 && queryKey[0] === QueryKeyAuth)) {
        queryClient.removeQueries(queryKey);
      }
    }
  }
  queryClient.setQueryData([QueryKeyAuth], data);
  const encryptData = CryptoJS.AES.encrypt(JSON.stringify(data), STORE_SECRET).toString();

  if (checkIsClientView() || isFromAdmin) {
    sessionStorage.setItem(BROWSER_TOKEN_STORAGE_KEY, encryptData);
  } else {
    localStorage.setItem(BROWSER_TOKEN_STORAGE_KEY, encryptData);
  }
};

export const clearAuth = (redirect?: boolean) => {
  queryClient.setQueryData([QueryKeyAuth], null);

  clearStorageBrandId();
  if (checkIsClientView()) {
    sessionStorage.removeItem(BROWSER_TOKEN_STORAGE_KEY);
  } else {
    localStorage.removeItem(BROWSER_TOKEN_STORAGE_KEY);
  }

  if (redirect) {
    window.location.replace('/');
  }
};

export const fetchAuth = async () => await queryClient.fetchQuery([QueryKeyAuth], _getAuth);
export const getCachedAuthData = () => queryClient.getQueryData<AuthProtocolsLoginResponse>([QueryKeyAuth]);
export const refreshAuth = async (refresh_token: string) => {
  const response = await authApi.refreshTokenCreate({
    refresh_token,
  });

  const { access_token } = response.data;

  _setAuth(
    {
      refresh_token,
      access_token,
    },
    false
  );

  return access_token;
};

export const useAuth = (): UseQueryResult<AuthProtocolsLoginResponse | null> => useQuery([QueryKeyAuth], _getAuth);
export const useLoginMutation = () => {
  return useMutation(authApi.loginCreate, {
    onSuccess: (resp) => {
      if (!resp) return;
      _setAuth(resp.data, true);
    },
  });
};
export const useLogoutMutation = () => {
  return useMutation(authApi.logoutCreate, {
    onSuccess: () => {
      clearAuth();
    },
  });
};

/**
 * opscc나 brand-client-view에서 받은 토큰 + 목적지 client_id 조합으로 새로운 토큰을 만든다
 */
export const createClientViewAuthorization = async (
  accessToken: string,
  refreshToken: string,
  request: AuthProtocolsCreateClientViewAuthorizationRequest
) => {
  // 이전 토큰으로 요청
  _setAuth(
    {
      access_token: accessToken,
      refresh_token: refreshToken,
    },
    true,
    true // isFromAdmin flag
  );
  const res = await authApi.clientViewAuthorizationCreate(request);
  if (res.status === 200) {
    // 새 토큰으로 갈아끼운다.
    _setAuth(
      {
        access_token: res.data.access_token,
        refresh_token: res.data.refresh_token,
      },
      true,
      true // isFromAdmin flag
    );
    return 'success';
  }
  return 'failed';
};

export const useLoginRiotOkta = () => {
  const { data: authData } = useAuth();

  const { mutateAsync } = useLogoutMutation();

  return useMutation({
    mutationFn: async (request: AuthProtocolsOktaRiotLoginRequest) => {
      if (authData?.access_token) {
        await mutateAsync({
          access_token: authData.access_token,
        });
      }
      return authApi.oktaCreate(request);
    },
    onSuccess: (resp) => {
      if (!resp) return;
      _setAuth(resp.data, true);
    },
  });
};

export const useLoginGeneralOkta = () => {
  const { data: authData } = useAuth();

  const { mutateAsync } = useLogoutMutation();

  return useMutation({
    mutationFn: async (request: AuthProtocolsOktaLoginRequest) => {
      if (authData?.access_token) {
        await mutateAsync({
          access_token: authData.access_token,
        });
      }
      return authApi.loginCreate2(request);
    },
    onSuccess: (resp) => {
      if (!resp) return;
      _setAuth(resp.data, true);
    },
  });
};

export const useLoginOktaSaml = () => {
  const { data: authData } = useAuth();

  const { mutateAsync } = useLogoutMutation();

  return useMutation({
    mutationFn: async (request: AuthProtocolsOktaSamlLoginRequest) => {
      if (authData?.access_token) {
        await mutateAsync({
          access_token: authData.access_token,
        });
      }
      return authApi.samlLoginCreate(request);
    },
    onSuccess: (resp) => {
      if (!resp) return;
      _setAuth(resp.data, true);
    },
  });
};
// #endregion

// #region UserInfo 관련 로직
const USER_INFO_STALE_TIME = 1000 * 60 * 5; // 5 minutes
type UseUserInfoQuery = AuthProtocolsGetUserInfoResponse & {
  isAdmin: boolean;
  channelDisplayType: ChannelDisplayTypeEnum;
  clientName: string;
  use_piracy: boolean;
};
export const useUserInfoQuery = (
  options?: UseQueryOptions<UseUserInfoQuery>
): UseQueryResult<UseUserInfoQuery, unknown> => {
  const { data } = useAuth();

  return useQuery({
    queryKey: [QueryKeyAuth, 'user-info'],
    queryFn: async () => {
      try {
        const { data } = await authApi.userInfoList();
        return {
          ...data,
          isAdmin: data.role === MarqCommonEnumsClientClientRoleEnum.Admin,
          channelDisplayType: parseChannelDisplayTypeEnum(data),
          clientName: data.client_name,
          use_piracy: data.customer_type?.primary === 'piracy',
        };
      } catch (error: any) {
        if (error?.response?.status >= 500) {
          queryClient.setQueryData([QueryKeyAuth, 'user-info'], null);
          queryClient.setQueryData([QueryKeyAuth, 'user-info-error'], true);
        }
        throw error;
      }
    },
    enabled: !!data?.access_token && !queryClient.getQueryData([QueryKeyAuth, 'user-info-error']),
    ...options,
    staleTime: USER_INFO_STALE_TIME,
  });
};
export const getUserInfoQuery = async () => {
  let data = queryClient.getQueryData<UseUserInfoQuery>([QueryKeyAuth, 'user-info']);
  if (!data) {
    try {
      data = await queryClient.fetchQuery({
        queryKey: [QueryKeyAuth, 'user-info'],
        queryFn: async () => {
          const { data } = await authApi.userInfoList();
          return {
            ...data,
            isAdmin: data.role === MarqCommonEnumsClientClientRoleEnum.Admin,
            channelDisplayType: parseChannelDisplayTypeEnum(data),
            use_piracy: data.customer_type?.primary === 'piracy',
          };
        },
        staleTime: USER_INFO_STALE_TIME,
      });
    } catch {
      // do nothing
    }
  }
  return data;
};

export const useGetEmployeeDataWithTokenQuery = (request: EmployeeSignupProtocolsGetEmployeeDataWithTokenRequest) =>
  useQuery(
    [QueryKeyAuth, 'employee-signup', request.token],
    async () => {
      const { data } = await employeeSignupApi.getEmployeeDataWithTokenCreate(request);
      return {
        ...data,
        language: data.language || 'en-US',
      };
    },
    { retry: false, enabled: false }
  );

export const useUpdateRegisterMutation = () => {
  return useMutation(employeeSignupApi.updateRegisterCreate, {
    onSuccess: (resp) => {
      if (resp.data.access_token === null) {
        clearAuth(false);
      }

      _setAuth(resp.data, true);
    },
  });
};

//clientViewClientsCreate
export const useClientViewClientsQuery = <TData>(
  options?: UseQueryOptions<AuthProtocolsGetClientViewClientsResponse, unknown, TData>
) =>
  useQuery<AuthProtocolsGetClientViewClientsResponse, unknown, TData>({
    queryKey: [QueryKeyAuth, 'client-view-clients'],
    queryFn: async () => {
      const { data } = await authApi.clientViewClientsCreate({});
      return data;
    },
    ...options,
  });

export const useVerifyEmailCreateQuery = <TData = AuthProtocolsVerifyEmailResponse>(
  request: AuthProtocolsVerifyEmailRequest,
  options?: UseQueryOptions<AuthProtocolsVerifyEmailResponse, unknown, TData>
) =>
  useQuery<AuthProtocolsVerifyEmailResponse, unknown, TData>({
    queryKey: [QueryKeyAuth, 'verify-email', request.uuid],
    queryFn: async () => {
      const { data } = await authApi.verifyEmailCreate(request);
      return data;
    },
    ...options,
  });

export const useRegisterOtpMutation = () => useMutation(authApi.registerOtpCreate);

export const useResendVerifyEmailMutation = () => useMutation(authApi.resendVerificationEmailCreate);
